import React from 'react'
import { useSelector } from 'react-redux'
import { snakeCase } from 'lodash'
import RootState from '../../../interfaces/RootState'
import SelectField from '../../common/SelectField'
import IssuerRemoteOptionsSelectField from '../../common/IssuerRemoteOptionsSelectField'

type Props = {
  handleChange: (field: string, value: string) => void,
  values: {
    [key: string]: string
  }
}

const PlanMetadataFields: React.FC<Props> = ({ handleChange, values }) => {
  const audiencePlanTypes = (state: RootState) => state.login.session.configuration.benefits.audience_plan_types
  const privateExchanges = (state: RootState) => state.login.session.configuration.benefits.private_exchange
  const audiencePlanTypeOptions = useSelector(audiencePlanTypes)
  const privateExchangeOptions = useSelector(privateExchanges)

  const planMetadataValues = () => {
    if (['age_29_rider', 'wellness'].includes(values.plan_metadata_field)) {
      return [{ text: 'Yes', value: 'true' }, { text: 'No', value: 'false' }]
    }

    if (['voluntary'].includes(values.plan_metadata_field)) {
      return ['Yes', 'No', 'Not Applicable']
    }

    if (values.plan_metadata_field === 'plan_type' && values.audience) {
      return audiencePlanTypeOptions[values.audience]
    }

    if (values.plan_metadata_field === 'term') {
      return [{ text: 'Short', value: 'short_term' }, { text: 'Long', value: 'long_term' }]
    }

    if (values.plan_metadata_field === 'private_exchange') {
      return privateExchangeOptions.concat('None')
    }

    return []
  }

  const productLineMetadataFields = (): { [key: string]: string[] } => {
    return {
      dental: ['plan_type'],
      disability: ['term'],
      medical: ['age_29_rider', 'plan_type', 'private_exchange'],
      vision: ['plan_type'],
      accident: ['wellness'],
      hospital_indemnity: ['wellness'],
      critical_illness: ['wellness']
    }
  }

  const fieldOptionTexts = (): { [key: string]: string } => {
    return {
      voluntary: 'Voluntary',
      age_29_rider: 'Age 29 Rider',
      plan_type: 'Plan Type',
      private_exchange: 'Private Exchange',
      term: 'Term',
      wellness: 'Wellness'
    }
  }

  const baseMetadataFields = () => {
    if (values.line_of_coverage === 'Medical') {
      return []
    }

    return [
      'voluntary'
    ]
  }

  const metadataOptions = () => {
    const productLineFields = productLineMetadataFields()[snakeCase(values.line_of_coverage)]
    if (productLineFields === undefined) {
      return baseMetadataFields()
    }

    return baseMetadataFields().concat(productLineFields)
  }

  const metadataSelectOptions = () => {
    return metadataOptions().map((option) => {
      return { text: fieldOptionTexts()[option], value: option }
    })
  }

  return (
    <>
      <SelectField
        name="plan_metadata_field"
        label="Plan Metadata Field"
        handleChange={handleChange}
        options={metadataSelectOptions()}
        value={values.plan_metadata_field}
      />

      <SelectField
        name="plan_metadata_value"
        label="Plan Metadata Value"
        handleChange={handleChange}
        options={planMetadataValues()}
        value={values.plan_metadata_value}
      />

      <IssuerRemoteOptionsSelectField
        handleChange={handleChange}
        name="issuers"
        recordValue="issuer_id"
        value={values.issuers}
        multi
      />
    </>
  )
}

export default PlanMetadataFields
